import moment from 'moment';
export const periodTypesMap = {
  allTime: {
    titleKey: 'commons.periods.all_time'
  },
  week: {
    titleKey: 'commons.periods.week',
    alternativeTitleKey: 'commons.periods.this_week',
    enableInDashboard: true
  },
  month: {
    titleKey: 'commons.periods.month',
    alternativeTitleKey: 'commons.periods.this_month',
    enableInDashboard: true
  },
  year: {
    titleKey: 'commons.periods.year',
    alternativeTitleKey: 'commons.periods.this_year',
    enableInDashboard: true
  },
  today: {
    titleKey: 'commons.periods.today'
  },
  '7days': {
    titleKey: 'commons.periods.7days'
  },
  '30days': {
    titleKey: 'commons.periods.30days'
  },
  '12months': {
    titleKey: 'commons.periods.12months'
  },
  custom: {
    titleKey: 'commons.periods.custom'
  }
};
export const periodTypes = [{
  key: 'allTime',
  ...periodTypesMap.allTime
}, {
  key: 'week',
  ...periodTypesMap.week
}, {
  key: 'month',
  ...periodTypesMap.month
}, {
  key: 'year',
  ...periodTypesMap.year
}, {
  key: 'today',
  ...periodTypesMap.today
}, {
  key: '7days',
  ...periodTypesMap['7days']
}, {
  key: '30days',
  ...periodTypesMap['30days']
}, {
  key: '12months',
  ...periodTypesMap['12months']
}, {
  key: 'custom',
  ...periodTypesMap.custom
}];
const now = moment().endOf('day');
export class Period {
  constructor(type, from, to) {
    this.type = type;
    this.from = from;
    this.to = to;
    this.setTitle();
  }
  setTitle() {
    switch (this.type) {
      case 'allTime':
        this.titleKey = 'commons.periods.all_time';
        break;
      case 'week':
        this.titleKey = 'commons.periods.week_number';
        this.alternativeTitleKey = 'commons.periods.this_week';
        break;
      case 'month':
        this.title = this.from.format('MMMM YYYY');
        this.alternativeTitleKey = 'commons.periods.this_month';
        break;
      case 'year':
        this.title = this.from.format('YYYY');
        this.alternativeTitleKey = 'commons.periods.this_year';
        break;
      case 'today':
        this.titleKey = 'commons.periods.today';
        break;
      case '7days':
        this.titleKey = 'commons.periods.7days';
        break;
      case '30days':
        this.titleKey = 'commons.periods.30days';
        break;
      case '12months':
        this.titleKey = 'commons.periods.12months';
        break;
      case 'custom':
        this.titleKey = 'commons.periods.from_to';
        break;
      default:
        break;
    }
  }
  get nbDays() {
    return Math.ceil(this.to.diff(this.from, 'days', true));
  }
  get nbSpentDays() {
    if (this.from.isAfter(now)) return 0;
    if (this.to.isAfter(now)) return Math.ceil(now.diff(this.from, 'days', true));
    return Math.ceil(this.to.diff(this.from, 'days', true));
  }
  get unit() {
    switch (this.type) {
      case 'week':
      case 'month':
      case 'today':
      case '7days':
      case '30days':
        return 'day';
      case 'year':
      case '12months':
        return 'month';
      default:
        return this.to.diff(this.from, 'months') >= 2 ? 'month' : 'day';
    }
  }
  get times() {
    const _times = [];
    const date = moment(this.from.valueOf());
    _times.push(date.valueOf());
    if (this.type === 'year' || this.type === '12months') {
      for (let i = 0; i < 12; ++i) {
        date.add(1, 'months');
        _times.push(date.valueOf());
      }
    } else {
      for (let i = 0; i < this.nbDays; ++i) {
        date.add(1, 'days');
        _times.push(date.valueOf());
      }
    }
    return _times;
  }
  get hasNext() {
    return this.to.isBefore(moment());
  }
  getPrevPeriod() {
    switch (this.type) {
      case 'week':
        return new Period('week', moment(this.from).add(-7, 'days').startOf('isoWeek'), moment(this.from).add(-7, 'days').endOf('isoWeek'));
      case 'month':
        return new Period('month', moment(this.from).add(-1, 'months').startOf('month'), moment(this.from).add(-1, 'months').endOf('month'));
      case 'year':
        return new Period('year', moment(this.from).add(-1, 'years').startOf('year'), moment(this.from).add(-1, 'years').endOf('year'));
      case 'custom':
      case '30days':
      case '7days':
        return new Period('custom', moment(this.from).add(-this.nbDays, 'days').startOf('day'), moment(this.to).add(-this.nbDays, 'days').endOf('day'));
      default:
        throw new Error('prev period not defined');
    }
  }
  getNextPeriod() {
    switch (this.type) {
      case 'week':
        return new Period('week', moment(this.from).add(7, 'days').startOf('isoWeek'), moment(this.from).add(7, 'days').endOf('isoWeek'));
      case 'month':
        return new Period('month', moment(this.from).add(1, 'months').startOf('month'), moment(this.from).add(1, 'months').endOf('month'));
      case 'year':
        return new Period('year', moment(this.from).add(1, 'years').startOf('year'), moment(this.from).add(1, 'years').endOf('year'));
      case 'custom':
      case '30days':
      case '7days':
        return new Period('custom', moment(this.from).add(this.nbDays, 'days').startOf('day'), moment(this.to).add(this.nbDays, 'days').endOf('day'));
      default:
        throw new Error('prev period not defined');
    }
  }
  toIPeriod() {
    return {
      period: 'custom',
      from: this.from,
      to: this.to,
      unit: this.unit
    };
  }
  clone() {
    return new Period(this.type, this.from.clone(), this.to.clone());
  }
  locale(locale) {
    this.from.locale(locale);
    this.to.locale(locale);
    this.setTitle();
    return this.clone();
  }
}
export const today = new Period('today', moment().startOf('day'), moment());
export const currentWeek = new Period('week', moment().startOf('isoWeek'), moment().endOf('isoWeek'));
export const prevWeek = new Period('week', moment().add(-1, 'week').startOf('isoWeek'), moment().add(-1, 'week').endOf('isoWeek'));
export const currentMonth = new Period('month', moment().startOf('month'), moment().endOf('month'));
export const prevMonth = new Period('month', moment().add(-1, 'month').startOf('month'), moment().add(-1, 'month').endOf('month'));
export const currentYear = new Period('year', moment().startOf('year'), moment().endOf('year'));
export const lastYear = new Period('year', moment().add(-1, 'year').startOf('year'), moment().add(-1, 'year').endOf('year'));
export const last7Days = new Period('7days', moment().add(-7, 'days').startOf('day'), moment());
export const last30Days = new Period('30days', moment().add(-30, 'days').startOf('day'), moment());
export const last12Months = new Period('12months', moment().add(-12, 'months').startOf('day'), moment());
export function setLocales(locale) {
  today.locale(locale);
  currentWeek.locale(locale);
  prevWeek.locale(locale);
  currentMonth.locale(locale);
  prevMonth.locale(locale);
  currentYear.locale(locale);
  last7Days.locale(locale);
  last30Days.locale(locale);
  last12Months.locale(locale);
}
export function periodToParams({
  period,
  from,
  to,
  unit
}) {
  return [{
    key: 'period',
    value: period
  }, {
    key: 'date_start',
    value: from.format('DD-MM-YYYY')
  }, {
    key: 'date_end',
    value: to.format('DD-MM-YYYY')
  }, {
    key: 'unit',
    value: unit
  }];
}